// File: src/components/SlotTemplates.jsx
// Modified for use inside Templates.jsx wrapper
// Buttons moved to preview-panel for better layout

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Calendar, Clock, ChevronLeft, ChevronRight, Settings, Save,
  Plus, Trash2, Coffee, UserX, Download, List, Repeat1, Sparkles
} from 'lucide-react';
import { bookingTemplateAPI } from '../services/api';
import { addMonths, getTodayDate, getMondayOfCurrentWeek, formatDateGB } from '../utils/DateUtils';
import { AUTH0_NAMESPACE, DAYS_OF_WEEK, SLOT_DURATIONS, BREAK_DURATIONS, DEFAULT_SETTINGS } from '../utils/constants';
import '../styles/unified.css';

export default function SlotTemplates() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');

  const today = getTodayDate();
  const futureDate = addMonths(new Date(), DEFAULT_SETTINGS.DATE_RANGE_MONTHS);

  const [settings, setSettings] = useState({
    slotName: `Myslot-${formatDateGB(new Date())}-${user?.name || 'ServiceProvider'}`,
    dateFrom: today,
    dateTo: futureDate,
    dayFrom: DEFAULT_SETTINGS.DEFAULT_DAY_FROM,
    dayTo: DEFAULT_SETTINGS.DEFAULT_DAY_TO,
    startTime: DEFAULT_SETTINGS.DEFAULT_START_TIME,
    endTime: DEFAULT_SETTINGS.DEFAULT_END_TIME,
    slotDuration: DEFAULT_SETTINGS.DEFAULT_SLOT_DURATION,
    breakDuration: DEFAULT_SETTINGS.DEFAULT_BREAK_DURATION,
    daysInAdvance: DEFAULT_SETTINGS.DEFAULT_DAYS_IN_ADVANCE,
    myBreakFrom: '',
    myBreakTo: ''
  });

  const [privateAppointments, setPrivateAppointments] = useState([]);
  const [exceptionalSlots, setExceptionalSlots]       = useState([]);
  const [currentWeekStart, setCurrentWeekStart]       = useState(getMondayOfCurrentWeek());
  const [hasChanges, setHasChanges]                   = useState(false);
  const [leftTab, setLeftTab]                           = useState('settings'); // 'settings' | 'private' | 'exceptional'
  const [savedTemplates, setSavedTemplates]           = useState([]);
  const [currentTemplateId, setCurrentTemplateId]     = useState(null);
  const [currentTemplateIsActive, setCurrentTemplateIsActive] = useState(null);
  const [isLoading, setIsLoading]                     = useState(false);
  const [showTemplateList, setShowTemplateList]       = useState(false);
  const [saveMessage, setSaveMessage]                 = useState(null);

  useEffect(() => {
    if (isServiceProvider) {
      loadUserTemplates();
    }
  }, [isServiceProvider]);

  // ── Template CRUD ─────────────────────────────────────────────────────────

  const loadUserTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await bookingTemplateAPI.getUserTemplates(getAccessTokenSilently);
      if (response.success) {
        setSavedTemplates(response.data);
        if (!currentTemplateId && response.data.length > 0) {
          const activeTemplate = response.data.find(t => t.isActive);
          if (activeTemplate) {
            loadTemplate(activeTemplate);
            showMessage(`Active template loaded: ${activeTemplate.templateName}`, 'success');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      showMessage('Failed to load templates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!isServiceProvider) {
      showMessage('Only ServiceProvider/Instructors can save templates', 'error');
      return;
    }

    // ── Pre-save validation ──────────────────────────────────────────────────
    const validationErrors = [];
    const fmtDateLabel = (d) =>
      new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
      });

    // 1. My Daily Break — From must be before To
    if (settings.myBreakFrom && settings.myBreakTo) {
      if (timeToMinutes(settings.myBreakTo) <= timeToMinutes(settings.myBreakFrom)) {
        validationErrors.push(
          `My Daily Break: "To" time (${formatTime12hr(settings.myBreakTo)}) must be later than "From" time (${formatTime12hr(settings.myBreakFrom)})`
        );
      }
    }

    // 2. Private Appointments — Time To must be after Time From
    privateAppointments.forEach((apt, i) => {
      if (apt.timeFrom && apt.timeTo) {
        if (timeToMinutes(apt.timeTo) <= timeToMinutes(apt.timeFrom)) {
          validationErrors.push(
            `Private Appointment ${i + 1} (${fmtDateLabel(apt.date)}): "Time To" (${formatTime12hr(apt.timeTo)}) must be later than "Time From" (${formatTime12hr(apt.timeFrom)})`
          );
        }
      }
    });

    // 3. Exceptional Slots — each window's End must be after Start
    exceptionalSlots.forEach((es) => {
      (es.windows || []).forEach((w, wi) => {
        if (w.startTime && w.endTime) {
          if (timeToMinutes(w.endTime) <= timeToMinutes(w.startTime)) {
            validationErrors.push(
              `Exceptional Slot ${fmtDateLabel(es.date)} — Window ${wi + 1}: "End" (${formatTime12hr(w.endTime)}) must be later than "Start" (${formatTime12hr(w.startTime)})`
            );
          }
        }
      });
    });

    // 4. No date in both Private Appointments and Exceptional Slots
    const privateDatesSet   = new Set(privateAppointments.map(apt => apt.date));
    const exceptionalDates  = exceptionalSlots.map(es => es.date);
    const clashDates        = exceptionalDates.filter(d => privateDatesSet.has(d)).sort();
    if (clashDates.length > 0) {
      validationErrors.push(
        `${clashDates.length === 1 ? 'Date appears' : 'Dates appear'} in both Private Appointments and Exceptional Slots: ${clashDates.map(fmtDateLabel).join(', ')}`
      );
    }

    if (validationErrors.length > 0) {
      showMessage(
        'Cannot save — please fix the following: • ' + validationErrors.join('• '), 'error' );
      return;
    }

    setIsLoading(true);
    try {
      const cleanedAppointments = privateAppointments.map(({ isNew, ...apt }) => apt);
      const cleanedExceptional  = exceptionalSlots.map(({ isNew, ...es }) => ({
        ...es,
        windows: es.windows.map(({ isNew: _iN, ...w }) => w)
      }));

      const templateData = {
        settings,
        privateAppointments: cleanedAppointments,
        exceptionalSlots:    cleanedExceptional
      };

      let response;
      if (currentTemplateId) {
        response = await bookingTemplateAPI.updateTemplate(currentTemplateId, templateData, getAccessTokenSilently);
        showMessage('Template updated successfully!', 'success');
      } else {
        response = await bookingTemplateAPI.saveTemplate(templateData, getAccessTokenSilently);
        setCurrentTemplateId(response.data.templateId);
        setCurrentTemplateIsActive(response.data.isActive);
        showMessage(`Template saved successfully as ${response.data.isActive ? 'ACTIVE' : 'INACTIVE'}!`, 'success');
      }

      setHasChanges(false);
      await loadUserTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      showMessage(error.message || 'Failed to save template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplate = (template) => {
    setSettings(template.settings);
    setPrivateAppointments(template.privateAppointments || []);
    setExceptionalSlots(template.exceptionalSlots || []);
    setCurrentTemplateId(template.templateId);
    setCurrentTemplateIsActive(template.isActive);
    setHasChanges(false);
    setShowTemplateList(false);
    showMessage(`Editing: ${template.templateName}`, 'success');
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete "${templateName}"?`)) return;

    setIsLoading(true);
    try {
      await bookingTemplateAPI.deleteTemplate(templateId, getAccessTokenSilently);
      if (templateId === currentTemplateId) {
        resetToNewTemplate();
      }
      await loadUserTemplates();
      showMessage('Template deleted successfully', 'success');
    } catch (error) {
      showMessage(error.message || 'Failed to delete template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetToNewTemplate = () => {
    if (savedTemplates.length >= 2) {
      showMessage('Maximum 2 templates allowed', 'error');
      return;
    }

    const today      = getTodayDate();
    const futureDate = addMonths(new Date(), DEFAULT_SETTINGS.DATE_RANGE_MONTHS);

    setSettings({
      slotName:      `Myslot-${formatDateGB(new Date())}-${user?.name || 'ServiceProvider'}`,
      dateFrom:      today,
      dateTo:        futureDate,
      dayFrom:       DEFAULT_SETTINGS.DEFAULT_DAY_FROM,
      dayTo:         DEFAULT_SETTINGS.DEFAULT_DAY_TO,
      startTime:     DEFAULT_SETTINGS.DEFAULT_START_TIME,
      endTime:       DEFAULT_SETTINGS.DEFAULT_END_TIME,
      slotDuration:  DEFAULT_SETTINGS.DEFAULT_SLOT_DURATION,
      breakDuration: DEFAULT_SETTINGS.DEFAULT_BREAK_DURATION,
      daysInAdvance: DEFAULT_SETTINGS.DEFAULT_DAYS_IN_ADVANCE,
      myBreakFrom:   '',
      myBreakTo:     ''
    });
    setPrivateAppointments([]);
    setExceptionalSlots([]);
    setCurrentTemplateId(null);
    setCurrentTemplateIsActive(null);
    setHasChanges(false);
    showMessage('New template created', 'success');
  };

  const showMessage = (message, type) => {
    setSaveMessage({ message, type });
    if (type !== 'error') {
      setTimeout(() => setSaveMessage(null), 3000);
    }
    // errors persist until the user dismisses them via the close button
  };

  // ── Settings ──────────────────────────────────────────────────────────────

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  // ── Private appointments ──────────────────────────────────────────────────

  const addPrivateAppointment = () => {
    setPrivateAppointments([...privateAppointments, {
      id:        Date.now(),
      date:      new Date().toISOString().split('T')[0],
      timeFrom:  '12:00',
      timeTo:    '13:00',
      dayOff:    false,
      morning:   false,
      afternoon: false,
      isNew:     true
    }]);
    setHasChanges(true);
  };

  const repeatLastAppointment = () => {
    if (privateAppointments.length === 0) return;
    const last    = privateAppointments[privateAppointments.length - 1];
    const lastDate = new Date(last.date);
    lastDate.setDate(lastDate.getDate() + 1);
    setPrivateAppointments([...privateAppointments, {
      id:        Date.now(),
      date:      lastDate.toISOString().split('T')[0],
      timeFrom:  last.timeFrom,
      timeTo:    last.timeTo,
      dayOff:    last.dayOff    || false,
      morning:   last.morning   || false,
      afternoon: last.afternoon || false,
      isNew:     true
    }]);
    setHasChanges(true);
  };

  const updatePrivateAppointment = (id, field, value) => {
    setPrivateAppointments(prev =>
      prev.map(apt => apt.id === id ? { ...apt, [field]: value } : apt)
    );
    setHasChanges(true);
  };

  const handleCheckboxChange = (id, checkboxType, isChecked) => {
    setPrivateAppointments(prev =>
      prev.map(apt => {
        if (apt.id !== id) return apt;
        const u = { ...apt };
        if (checkboxType === 'dayOff') {
          u.dayOff    = isChecked; u.morning   = false; u.afternoon = false;
          if (isChecked) { u.timeFrom = settings.startTime; u.timeTo = settings.endTime; }
        } else if (checkboxType === 'morning') {
          u.morning   = isChecked; u.dayOff    = false; u.afternoon = false;
          if (isChecked) { u.timeFrom = settings.startTime; u.timeTo = settings.myBreakFrom || '12:00'; }
        } else if (checkboxType === 'afternoon') {
          u.afternoon = isChecked; u.dayOff    = false; u.morning   = false;
          if (isChecked) { u.timeFrom = settings.myBreakFrom || '12:00'; u.timeTo = settings.endTime; }
        }
        return u;
      })
    );
    setHasChanges(true);
  };

  const deletePrivateAppointment = (id) => {
    setPrivateAppointments(prev => prev.filter(apt => apt.id !== id));
    setHasChanges(true);
  };

  // ── Exceptional slots ─────────────────────────────────────────────────────

  const addExceptionalSlot = () => {
    const newDate = new Date().toISOString().split('T')[0];
    setExceptionalSlots(prev => [...prev, {
      id:      Date.now(),
      date:    newDate,
      windows: [{ id: Date.now() + 1, startTime: '09:00', endTime: '12:00' }],
      isNew:   true
    }]);
    setHasChanges(true);
  };

  const removeExceptionalSlot = (slotId) => {
    setExceptionalSlots(prev => prev.filter(es => es.id !== slotId));
    setHasChanges(true);
  };

  const updateExceptionalSlotDate = (slotId, date) => {
    setExceptionalSlots(prev =>
      prev.map(es => es.id === slotId ? { ...es, date } : es)
    );
    setHasChanges(true);
  };

  const addWindow = (slotId) => {
    setExceptionalSlots(prev =>
      prev.map(es => {
        if (es.id !== slotId) return es;
        const lastWin = es.windows[es.windows.length - 1];
        // Default new window starts 30 min after last window ends
        const newStart = lastWin ? lastWin.endTime : '09:00';
        const [h, m]   = newStart.split(':').map(Number);
        const afterMin = h * 60 + m + 30;
        const ns = `${String(Math.floor(afterMin / 60)).padStart(2,'0')}:${String(afterMin % 60).padStart(2,'0')}`;
        const ne = `${String(Math.floor((afterMin + 60) / 60)).padStart(2,'0')}:${String((afterMin + 60) % 60).padStart(2,'0')}`;
        return {
          ...es,
          windows: [...es.windows, { id: Date.now(), startTime: ns, endTime: ne }]
        };
      })
    );
    setHasChanges(true);
  };

  const removeWindow = (slotId, windowId) => {
    setExceptionalSlots(prev =>
      prev.map(es => {
        if (es.id !== slotId) return es;
        if (es.windows.length <= 1) return es; // keep at least one window
        return { ...es, windows: es.windows.filter(w => w.id !== windowId) };
      })
    );
    setHasChanges(true);
  };

  const updateWindow = (slotId, windowId, field, value) => {
    setExceptionalSlots(prev =>
      prev.map(es => {
        if (es.id !== slotId) return es;
        return {
          ...es,
          windows: es.windows.map(w =>
            w.id === windowId ? { ...w, [field]: value } : w
          )
        };
      })
    );
    setHasChanges(true);
  };

  // ── Time helpers ──────────────────────────────────────────────────────────

  const timeToMinutes = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const minutesToTime = (min) =>
    `${String(Math.floor(min / 60)).padStart(2,'0')}:${String(min % 60).padStart(2,'0')}`;

  const formatTime12hr = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    return `${hour % 12 || 12}:${min.toString().padStart(2,'0')} ${period}`;
  };

  // Count bookable slots across all windows of an exceptional date entry
  const countWindowSlots = (windows) => (windows || []).length;

  // Format day name for the exceptional slot card header
  const formatExcDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // ── Preview helpers ───────────────────────────────────────────────────────

  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getExceptionalEntry = (date) => {
    const dateStr = getDateStr(date);
    return exceptionalSlots.find(es => es.date === dateStr) || null;
  };

  const isDateInRange = (date) => {
    const checkDate = new Date(date);
    return checkDate >= new Date(settings.dateFrom) && checkDate <= new Date(settings.dateTo);
  };

  const isDayEnabled = (date) => {
    const dayName  = DAYS_OF_WEEK[date.getDay()];
    const fromIdx  = DAYS_OF_WEEK.indexOf(settings.dayFrom);
    const toIdx    = DAYS_OF_WEEK.indexOf(settings.dayTo);
    const dayIdx   = DAYS_OF_WEEK.indexOf(dayName);
    return fromIdx <= toIdx
      ? dayIdx >= fromIdx && dayIdx <= toIdx
      : dayIdx >= fromIdx || dayIdx <= toIdx;
  };

  const isDateBookable = (date) => {
    const now = new Date(); now.setHours(0,0,0,0);
    const check = new Date(date); check.setHours(0,0,0,0);
    const advance = new Date(now); advance.setDate(now.getDate() + settings.daysInAdvance);
    return check >= now && check <= advance;
  };

  const isSlotBlocked = (date, slotStart, slotEnd) => {
    const slotStartMin = timeToMinutes(slotStart);
    const slotEndMin   = timeToMinutes(slotEnd);
    const dateStr      = getDateStr(date);
    for (const apt of privateAppointments) {
      if (apt.date === dateStr) {
        const aptStartMin = timeToMinutes(apt.timeFrom);
        const aptEndMin   = timeToMinutes(apt.timeTo);
        if (!(slotEndMin <= aptStartMin || slotStartMin >= aptEndMin)) {
          return { blocked: true, reason: 'Private' };
        }
      }
    }
    return { blocked: false };
  };

  // ── Regular time slot generator ───────────────────────────────────────────

  const generateTimeSlots = () => {
    const slots = [];
    const startMinutes = timeToMinutes(settings.startTime);
    const endMinutes   = timeToMinutes(settings.endTime);
    const slotDuration = settings.slotDuration;
    const breakDuration= settings.breakDuration;
    const hasMyBreak   = settings.myBreakFrom && settings.myBreakTo;
    const myBreakStart = hasMyBreak ? timeToMinutes(settings.myBreakFrom) : null;
    const myBreakEnd   = hasMyBreak ? timeToMinutes(settings.myBreakTo)   : null;

    let cur = startMinutes;
    let breakInserted = false;

    while (cur + slotDuration <= endMinutes) {
      if (hasMyBreak && !breakInserted && cur + slotDuration > myBreakStart) {
        if (cur < myBreakStart && myBreakStart - cur >= slotDuration) {
          slots.push({ start: minutesToTime(cur), end: minutesToTime(myBreakStart), type: 'slot' });
        }
        slots.push({ start: minutesToTime(myBreakStart), end: minutesToTime(myBreakEnd), type: 'break' });
        cur = myBreakEnd;
        breakInserted = true;
        continue;
      }
      slots.push({ start: minutesToTime(cur), end: minutesToTime(cur + slotDuration), type: 'slot' });
      cur += slotDuration + breakDuration;
    }
    return slots;
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const weekDates  = getWeekDates(currentWeekStart);
  const timeSlots  = generateTimeSlots();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {saveMessage && (
        <div className={`save-message ${saveMessage.type} ${saveMessage.type === 'error' ? 'save-message-dismissible' : ''}`}>
          <span className="save-message-text">{saveMessage.message}</span>
          {saveMessage.type === 'error' && (
            <button
              className="save-message-close"
              onClick={() => setSaveMessage(null)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Template list modal */}
      {isServiceProvider && showTemplateList && (
        <div className="template-modal-overlay" onClick={() => setShowTemplateList(false)}>
          <div className="template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Saved Templates</h3>
              <button onClick={() => setShowTemplateList(false)} className="modal-close">×</button>
            </div>
            <div className="template-list">
              {savedTemplates.length === 0 ? (
                <p className="empty-templates">No saved templates yet</p>
              ) : (
                savedTemplates.map(template => (
                  <div key={template.templateId} className={`template-item ${template.isActive ? 'template-active' : 'template-inactive'}`}>
                    <div className="template-info">
                      <div className="template-header">
                        <h4>{template.templateName}</h4>
                        <span className={template.isActive ? 'badge-active' : 'badge-inactive'}>
                          {template.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <p className="template-date">Updated: {new Date(template.updatedAt).toLocaleDateString()}</p>
                    </div>
                    <div className="template-actions">
                      <button onClick={() => loadTemplate(template)} className="btn-load">
                        <Download size={16} /> Load
                      </button>
                      <button
                        onClick={() => deleteTemplate(template.templateId, template.templateName)}
                        disabled={template.isActive}
                        className="btn-delete-small"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className="page-container">
      <div className="app-layout">

        {/* ── Settings panel (left) ── */}
        {isServiceProvider && (
          <div className="settings-panel">
            <div className="settings-card">
              {/* Left panel header */}
              <div className="settings-header">
                <div className="settings-title-wrapper">
                  <Settings className="settings-icon" size={20} />
                  <h2 className="settings-title">
                    {leftTab === 'settings'     && 'Slot Settings'}
                    {leftTab === 'private'       && 'Private Appointments'}
                    {leftTab === 'exceptional'   && 'Exceptional Slots'}
                  </h2>
                  {currentTemplateId && <span className="badge-editing">Editing</span>}
                  {currentTemplateId && currentTemplateIsActive !== null && (
                    <span className={currentTemplateIsActive ? 'badge-status-active' : 'badge-status-inactive'}>
                      {currentTemplateIsActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  )}
                </div>
                {hasChanges && (
                  <button onClick={saveTemplate} className="btn-save" disabled={isLoading}>
                    <Save size={16} />
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>

              {/* Left panel tab bar */}
              <div className="left-tab-bar">
                <button
                  className={`left-tab ${leftTab === 'settings' ? 'left-tab-active' : ''}`}
                  onClick={() => setLeftTab('settings')}
                  title="Slot Settings"
                >
                  <Settings size={13} /> Settings
                </button>
                <button
                  className={`left-tab ${leftTab === 'private' ? 'left-tab-active left-tab-private' : ''}`}
                  onClick={() => setLeftTab('private')}
                  title="Private Appointments"
                >
                  <UserX size={13} /> Private
                  {privateAppointments.length > 0 && (
                    <span className="left-tab-badge left-tab-badge-private">{privateAppointments.length}</span>
                  )}
                </button>
                <button
                  className={`left-tab ${leftTab === 'exceptional' ? 'left-tab-active left-tab-exceptional' : ''}`}
                  onClick={() => setLeftTab('exceptional')}
                  title="Exceptional Slots"
                >
                  <Sparkles size={13} /> Exceptional
                  {exceptionalSlots.length > 0 && (
                    <span className="left-tab-badge left-tab-badge-exceptional">{exceptionalSlots.length}</span>
                  )}
                </button>
              </div>

              {leftTab === 'settings' && <div className="settings-form">

                {/* ── General settings ── */}
                <div className="form-group">
                  <label className="form-label"><h3>Slot Name</h3></label>
                  <input type="text" value={settings.slotName} onChange={(e) => handleSettingChange('slotName', e.target.value)} className="form-input" />
                </div>

                <div className="form-group">
                  <label className="form-label">Date Range</label>
                  <div className="input-group-2">
                    <input type="date" value={settings.dateFrom} onChange={(e) => handleSettingChange('dateFrom', e.target.value)} className="form-input" />
                    <input type="date" value={settings.dateTo}   onChange={(e) => handleSettingChange('dateTo',   e.target.value)} className="form-input" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Available Days in a Week</label>
                  <div className="input-group-2">
                    <select value={settings.dayFrom} onChange={(e) => handleSettingChange('dayFrom', e.target.value)} className="form-select">
                      {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
                    </select>
                    <select value={settings.dayTo} onChange={(e) => handleSettingChange('dayTo', e.target.value)} className="form-select">
                      {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Operating Hours</label>
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="time-label">From&nbsp;</label>
                      <input type="time" value={settings.startTime} onChange={(e) => handleSettingChange('startTime', e.target.value)} className="form-input" />
                    </div>
                    <div className="time-input-wrapper">
                      <label className="time-label">To&nbsp;</label>
                      <input type="time" value={settings.endTime} onChange={(e) => handleSettingChange('endTime', e.target.value)} className="form-input" />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="form-label">Slot Duration</label>
                    </div>
                    <div className="time-input-wrapper">
                      <select value={settings.slotDuration} onChange={(e) => handleSettingChange('slotDuration', Number(e.target.value))} className="form-select">
                        {SLOT_DURATIONS.map(d => <option key={d.value} value={d.value}>{d.value} minutes</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="form-label">Break Between Slots</label>
                    </div>
                    <div className="time-input-wrapper">
                      <select value={settings.breakDuration} onChange={(e) => handleSettingChange('breakDuration', Number(e.target.value))} className="form-select">
                        {BREAK_DURATIONS.map(d => (
                          <option key={d.value} value={d.value}>{d.value === 0 ? 'No break' : `${d.value} minutes`}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="form-label">Days in Advance for Booking</label>
                    </div>
                    <div className="time-input-wrapper">
                      <input type="number" min="0" max="365" value={settings.daysInAdvance} onChange={(e) => handleSettingChange('daysInAdvance', Number(e.target.value))} className="form-input" />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">My Daily Break</label>
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="time-label">From&nbsp;</label>
                      <input type="time" value={settings.myBreakFrom} onChange={(e) => handleSettingChange('myBreakFrom', e.target.value)} className="form-input" />
                    </div>
                    <div className="time-input-wrapper">
                      <label className="time-label">To&nbsp;</label>
                      <input type="time" value={settings.myBreakTo} onChange={(e) => handleSettingChange('myBreakTo', e.target.value)} className="form-input" />
                    </div>
                  </div>
                </div>

              </div>}

              {/* ── Private Appointments tab ── */}
              {leftTab === 'private' && (
                <div className="left-tab-content">
                  <button onClick={addPrivateAppointment} className="btn-add" style={{ marginBottom: '0.75rem' }}>
                    <UserX size={16} /> Add Private Appointment
                  </button>
                  <div className="appointments-list">
                    {privateAppointments.length === 0 ? (
                      <p className="empty-appointments">No private appointments</p>
                    ) : (
                      privateAppointments.map((apt, index) => (
                        <div key={apt.id} className={`appointment-card ${apt.isNew ? 'appointment-card-new' : ''}`}>
                          <div className="appointment-fields">
                            <div className="appointment-field">
                              <label className="form-label-small">Date</label>
                              <input type="date" value={apt.date} onChange={(e) => updatePrivateAppointment(apt.id, 'date', e.target.value)} className="form-input-small" />
                            </div>
                            <div className="appointment-field">
                              <label className="form-label-small">Time From</label>
                              <input type="time" value={apt.timeFrom} onChange={(e) => updatePrivateAppointment(apt.id, 'timeFrom', e.target.value)} className="form-input-small" disabled={apt.dayOff || apt.morning || apt.afternoon} />
                            </div>
                            <div className="appointment-field">
                              <label className="form-label-small">Time To</label>
                              <input type="time" value={apt.timeTo} onChange={(e) => updatePrivateAppointment(apt.id, 'timeTo', e.target.value)} className="form-input-small" disabled={apt.dayOff || apt.morning || apt.afternoon} />
                            </div>
                          </div>
                          <div className="appointment-checkboxes">
                            <label className="checkbox-label">
                              <input type="checkbox" checked={apt.dayOff || false} onChange={(e) => handleCheckboxChange(apt.id, 'dayOff', e.target.checked)} disabled={apt.morning || apt.afternoon} className="checkbox-input" />
                              <span>&nbsp;Day Off</span>
                            </label>
                            <label className="checkbox-label">
                              <input type="checkbox" checked={apt.morning || false} onChange={(e) => handleCheckboxChange(apt.id, 'morning', e.target.checked)} disabled={apt.dayOff || apt.afternoon} className="checkbox-input" />
                              <span>&nbsp;Morning</span>
                            </label>
                            <label className="checkbox-label">
                              <input type="checkbox" checked={apt.afternoon || false} onChange={(e) => handleCheckboxChange(apt.id, 'afternoon', e.target.checked)} disabled={apt.dayOff || apt.morning} className="checkbox-input" />
                              <span>&nbsp;Afternoon</span>
                            </label>
                          </div>
                          <div className="appointment-actions">
                            {index === privateAppointments.length - 1 && (
                              <button onClick={repeatLastAppointment} className="btn-repeat" title="Repeat for next day">
                                <Repeat1 size={16} />
                              </button>
                            )}
                            <button onClick={() => deletePrivateAppointment(apt.id)} className="btn-delete">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* ── Exceptional Slots tab ── */}
              {leftTab === 'exceptional' && (
                <div className="left-tab-content">
                  <button onClick={addExceptionalSlot} className="btn-add" style={{ marginBottom: '0.75rem' }}>
                    <Plus size={16} /> Add Exceptional Date and Slots
                  </button>
                  {exceptionalSlots.length === 0 ? (
                    <p className="empty-appointments">No exceptional slots defined</p>
                  ) : (
                    <div className="appointments-list">
                      {exceptionalSlots
                        .slice()
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map((es) => (
                          <div key={es.id} className={`exc-date-card ${es.isNew ? 'appointment-card-new' : ''}`}>
                            <div className="exc-date-row">
                              <Calendar size={13} className="exc-icon" />
                              <input type="date" value={es.date} onChange={(e) => updateExceptionalSlotDate(es.id, e.target.value)} className="form-input-small" style={{ flex: 1 }} />
                              <span className="exc-date-badge">{formatExcDateLabel(es.date)}</span>
                              <button onClick={() => removeExceptionalSlot(es.id)} className="btn-delete" title="Remove this exceptional date">
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="exc-windows-label">
                              <Clock size={11} /> Time windows for this date
                            </div>
                            {es.windows.map((win, winIdx) => (
                              <div key={win.id}>
                                {winIdx > 0 && (() => {
                                  const prevEnd   = es.windows[winIdx - 1].endTime;
                                  const thisStart = win.startTime;
                                  const gapMin    = timeToMinutes(thisStart) - timeToMinutes(prevEnd);
                                  if (gapMin <= 0) return null;
                                  const hrs = Math.floor(gapMin / 60);
                                  const min = gapMin % 60;
                                  const label = hrs > 0
                                    ? `gap ${formatTime12hr(prevEnd)}–${formatTime12hr(thisStart)} (${hrs}h${min > 0 ? ` ${min}m` : ''})`
                                    : `gap ${formatTime12hr(prevEnd)}–${formatTime12hr(thisStart)} (${min}m)`;
                                  return (
                                    <div className="exc-gap-indicator">
                                      <div className="exc-gap-line" />
                                      <span>{label}</span>
                                      <div className="exc-gap-line" />
                                    </div>
                                  );
                                })()}
                                <div className="exc-window-row">
                                  <span className="exc-win-num">{winIdx + 1}</span>
                                  <div className="appointment-field">
                                    <label className="form-label-small">Start</label>
                                    <input type="time" value={win.startTime} onChange={(e) => updateWindow(es.id, win.id, 'startTime', e.target.value)} className="form-input-small" />
                                  </div>
                                  <div className="appointment-field">
                                    <label className="form-label-small">End</label>
                                    <input type="time" value={win.endTime} onChange={(e) => updateWindow(es.id, win.id, 'endTime', e.target.value)} className="form-input-small" />
                                  </div>
                                  <button onClick={() => removeWindow(es.id, win.id)} className="btn-delete" title="Remove window" disabled={es.windows.length <= 1} style={{ opacity: es.windows.length <= 1 ? 0.3 : 1 }}>
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <div className="exc-card-footer">
                              <button onClick={() => addWindow(es.id)} className="btn-add-window">
                                <Plus size={12} /> Add time window
                              </button>
                              <span className="exc-slot-count">
                                {countWindowSlots(es.windows)} slot{countWindowSlots(es.windows) !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {hasChanges && (
                <div className="unsaved-changes-alert">
                  <p className="unsaved-changes-text">
                    <div className="pulse-dot" />
                    Unsaved changes
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Preview panel (right) ── */}
        <div className={isServiceProvider ? 'preview-panel' : 'preview-panel-full'}>
          <div className="preview-card">


            <div className="preview-header">

              {isServiceProvider && (
                <div className="preview-actions">
                  <button onClick={() => setShowTemplateList(!showTemplateList)} className="btn-templates">
                    <List size={20} />
                    Templates ({savedTemplates.length})
                  </button>
                  <button
                    onClick={resetToNewTemplate}
                    className="btn-new"
                    disabled={savedTemplates.length >= 2}
                    title={savedTemplates.length >= 2 ? 'Maximum 2 templates allowed' : 'Create a new template'}
                  >
                    <Plus size={20} />
                    New Template
                  </button>
                </div>
              )}

              <div className="week-navigation">
                <h3 className="preview-title">Weekly Preview</h3>
                <button onClick={() => navigateWeek(-1)} className="nav-button"><ChevronLeft size={24} /></button>
                <span className="week-label">
                  {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – {weekDates[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <button onClick={() => navigateWeek(1)} className="nav-button"><ChevronRight size={24} /></button>
              </div>
            </div>

            <div className="grid-container">
              <div className="grid-wrapper">

                {/* Column headers */}
                <div className="grid-header">
                  <div className="time-header">Time</div>
                  {weekDates.map((date, idx) => {
                    const excEntry    = getExceptionalEntry(date);
                    const isExc       = !!excEntry;
                    const isInRange   = isDateInRange(date);
                    const isDayActive = isDayEnabled(date);
                    const isBookable  = isDateBookable(date);

                    if (isExc) {
                      const slotCount = countWindowSlots(excEntry.windows);
                      return (
                        <div key={idx} className="day-header day-header-exceptional">
                          <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0,3)}</div>
                          <div className="day-date day-date-exceptional">{date.getDate()}</div>
                          <div className="day-status day-status-exceptional">
                            <Sparkles size={10} /> Exc. ({slotCount})
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={idx} className={`day-header ${isInRange && isDayActive ? 'day-header-active' : 'day-header-inactive'}`}>
                        <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0,3)}</div>
                        <div className={`day-date ${isInRange && isDayActive ? 'day-date-active' : 'day-date-inactive'}`}>{date.getDate()}</div>
                        {isInRange && isDayActive && (
                          <div className={`day-status ${isBookable ? 'day-status-open' : 'day-status-future'}`}>
                            {isBookable ? '✓ Open' : 'Future'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Slot rows */}
                <div className="slots-container">
                  {timeSlots.map((slot, slotIdx) => (
                    <div key={slotIdx} className={`slot-row ${slot.type === 'break' ? 'break-row' : ''}`}>
                      <div className="time-cell">
                        <Coffee size={14} className={slot.type === 'break' ? 'break-icon' : 'time-icon'} />
                        <div className="time-range">
                          <div className="time-start">{formatTime12hr(slot.start)}</div>
                          <div className="time-end">{formatTime12hr(slot.end)}</div>
                        </div>
                      </div>

                      {slot.type === 'break' ? (
                        <div className="break-cell-full" style={{ gridColumn: '2 / -1' }}>
                          <div className="break-label">
                            <Coffee size={16} /><span>BREAK TIME</span>
                          </div>
                        </div>
                      ) : (
                        weekDates.map((date, dateIdx) => {
                          const excEntry    = getExceptionalEntry(date);
                          const isExc       = !!excEntry;
                          const isInRange   = isDateInRange(date);
                          const isDayActive = isDayEnabled(date);
                          const isBookable  = isDateBookable(date);

                          // ── Exceptional date ──────────────────────────────
                          if (isExc) {
                            return (
                              <div key={dateIdx} className="slot-cell slot-exceptional">
                                <div className="slot-status">
                                  <div className="status-exceptional"><Sparkles size={10} /> Exc.</div>
                                </div>
                              </div>
                            );
                          }

                          // ── Regular date ──────────────────────────────────
                          const isAvailable  = isInRange && isDayActive;
                          const blockCheck   = isSlotBlocked(date, slot.start, slot.end);
                          return (
                            <div key={dateIdx} className={`slot-cell ${!isAvailable ? 'slot-unavailable' : blockCheck.blocked ? 'slot-blocked' : isBookable ? 'slot-available' : 'slot-locked'}`}>
                              <div className="slot-status">
                                {!isAvailable ? (
                                  <div className="status-unavailable">-</div>
                                ) : blockCheck.blocked ? (
                                  <div className="status-blocked">{blockCheck.reason}</div>
                                ) : isBookable ? (
                                  <div className="status-available">Available</div>
                                ) : (
                                  <div className="status-locked">Locked</div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ))}
                </div>

                {timeSlots.length === 0 && (
                  <div className="empty-state">
                    <Clock size={48} className="empty-icon" />
                    <p className="empty-text">No time slots available</p>
                    <p className="empty-subtext">Adjust duration or operating hours</p>
                  </div>
                )}
              </div>
            </div>

            {/* Legend */}
            <div className="legend">
              <div className="legend-item">
                <div className="legend-box legend-box-available"></div>
                <span className="legend-text">Available to book</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-blocked"></div>
                <span className="legend-text">Blocked (Break/Private)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-locked"></div>
                <span className="legend-text">Future (not yet bookable)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-unavailable"></div>
                <span className="legend-text">Outside range/unavailable</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-exceptional"></div>
                <span className="legend-text">Exceptional slot</span>
              </div>
            </div>
            
            {/* Exceptional Slots — weekly summary */}
            {(() => {
              const weekDateStrs = new Set(weekDates.map(d => getDateStr(d)));
              const weekExcSlots = exceptionalSlots
                .filter(es => weekDateStrs.has(es.date))
                .sort((a, b) => a.date.localeCompare(b.date));

              if (weekExcSlots.length === 0) return null;

              return (
                <div className="exc-summary-section">
                  <div className="exc-summary-header">
                    <Sparkles size={15} className="exc-summary-icon" />
                    <span className="exc-summary-title">Exceptional Slots This Week</span>
                    <span className="exc-summary-count">
                      {weekExcSlots.length} date{weekExcSlots.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="exc-summary-rows">
                    {weekExcSlots.map((es) => {
                      const totalSlots = countWindowSlots(es.windows);
                      return (
                        <div key={es.date} className="exc-summary-row">
                          <span className="exc-row-date">{formatExcDateLabel(es.date)}</span>
                          <span className="exc-row-windows">
                            {(es.windows || [])
                              .slice()
                              .sort((a, b) => a.startTime.localeCompare(b.startTime))
                              .map((w, i) => (
                                <span key={i} className="exc-row-win">
                                  {formatTime12hr(w.startTime)}–{formatTime12hr(w.endTime)}
                                </span>
                              ))}
                          </span>
                          <span className="exc-row-slots">
                            ~{totalSlots} slot{totalSlots !== 1 ? 's' : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          </div>
        </div>

      </div>
      </div>
    </>
  );
}